import React, { useState } from 'react';
import {
  Box, VStack, Flex, Heading, Input, Button, Text, Badge, Divider, HStack,
  Alert, AlertIcon, Spinner, Table, Tbody, Tr, Td, Th, Thead,
  Card, CardHeader, CardBody, SimpleGrid, useToast, InputGroup, InputRightElement,
} from '@chakra-ui/react';
import { SearchIcon, CheckCircleIcon, WarningIcon, ArrowBackIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { consultarTramitePorFolio, validarDocumentoPorFolioValidacion } from '../../../api/opTramiteApi';

const ESTATUS_COLORS: Record<string, string> = {
  REGISTRADO: 'blue', EN_PROCESO: 'cyan', EN_REVISION: 'orange',
  OBSERVADO: 'yellow', PENDIENTE_DOCUMENTACION: 'purple', PENDIENTE_PAGO: 'pink',
  EN_INSPECCION: 'teal', PROCEDENTE: 'green', NO_PROCEDENTE: 'red',
  RECHAZADO: 'red', CONCLUIDO: 'teal', CANCELADO: 'red',
};

const ESTATUS_LABELS: Record<string, string> = {
  REGISTRADO: 'Registrado', EN_PROCESO: 'En Proceso', EN_REVISION: 'En Revisión',
  OBSERVADO: 'Observado', PENDIENTE_DOCUMENTACION: 'Pend. Documentación',
  PENDIENTE_PAGO: 'Pend. de Pago', EN_INSPECCION: 'En Inspección',
  PROCEDENTE: 'Procedente', NO_PROCEDENTE: 'No Procedente',
  RECHAZADO: 'Rechazado', CONCLUIDO: 'Concluido', CANCELADO: 'Cancelado',
};

const ConsultaFolioOP: React.FC = () => {
  const navigate = useNavigate();
  const [folio, setFolio] = useState('');
  const [folioValidacion, setFolioValidacion] = useState('');
  const [resultado, setResultado] = useState<any>(null);
  const [validacion, setValidacion] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [loadingVal, setLoadingVal] = useState(false);
  const [error, setError] = useState('');
  const [errorVal, setErrorVal] = useState('');
  const toast = useToast();

  const handleConsultar = async () => {
    if (!folio.trim()) {
      toast({ title: 'Ingrese un folio', status: 'warning', duration: 2000 });
      return;
    }
    setLoading(true);
    setError('');
    setResultado(null);
    try {
      const data = await consultarTramitePorFolio(folio.trim().toUpperCase());
      setResultado(data);
    } catch (err: any) {
      setError(err.message || 'No se encontró ningún trámite con ese folio');
    } finally {
      setLoading(false);
    }
  };

  const handleValidar = async () => {
    if (!folioValidacion.trim()) {
      toast({ title: 'Ingrese el folio de validación', status: 'warning', duration: 2000 });
      return;
    }
    setLoadingVal(true);
    setErrorVal('');
    setValidacion(null);
    try {
      const data = await validarDocumentoPorFolioValidacion(folioValidacion.trim().toUpperCase());
      setValidacion(data);
    } catch (err: any) {
      setErrorVal(err.message || 'Folio de validación no reconocido');
    } finally {
      setLoadingVal(false);
    }
  };

  return (
    <Box minH="100vh" bg="gray.50" py={8} px={{ base: 4, md: 10 }}>
      <VStack spacing={8} maxW="900px" mx="auto">
        {/* Navegación */}
        <Box w="full">
          <Button leftIcon={<ArrowBackIcon />} variant="ghost" size="sm" color="gray.500" onClick={() => navigate('/portal/obras')}>
            Portal Obras Públicas
          </Button>
        </Box>

        {/* ENCABEZADO */}
        <Box textAlign="center">
          <Box bg="#5b182b" borderRadius="full" w={16} h={16} mx="auto" mb={4}
            display="flex" alignItems="center" justifyContent="center">
            <SearchIcon color="white" boxSize={7} />
          </Box>
          <Heading size="lg" color="#5b182b">Portal Ciudadano</Heading>
          <Heading size="md" color="gray.600" fontWeight="normal">Consulta de Trámites de Obras Públicas</Heading>
          <Text color="gray.500" mt={1}>H. Ayuntamiento de Totolac · Tlaxcala</Text>
        </Box>

        {/* SECCIÓN 1: CONSULTA POR FOLIO */}
        <Card w="full" shadow="md">
          <CardHeader bg="gray.100" borderTopRadius="md">
            <Heading size="sm" color="#5b182b">Consultar Estatus de Trámite</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Ingrese el folio de su trámite (ej. OP-TOT-2026-A7F92K)
            </Text>
          </CardHeader>
          <CardBody>
            <InputGroup mb={4}>
              <Input
                placeholder="OP-TOT-2026-A7F92K"
                value={folio}
                onChange={e => setFolio(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleConsultar()}
                fontFamily="mono"
                textTransform="uppercase"
                fontSize="lg"
                size="lg"
              />
              <InputRightElement width="5rem" height="full">
                <Button size="sm" colorScheme="red" onClick={handleConsultar} isLoading={loading}>
                  Buscar
                </Button>
              </InputRightElement>
            </InputGroup>

            {loading && <Flex justify="center"><Spinner color="red.600" /></Flex>}

            {error && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {error}
              </Alert>
            )}

            {resultado && (
              <VStack align="stretch" spacing={4}>
                <Divider />
                <HStack justify="space-between" wrap="wrap">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="xs" color="gray.500">FOLIO</Text>
                    <Text fontFamily="mono" fontWeight="bold" fontSize="lg">{resultado.folio}</Text>
                  </VStack>
                  <Badge colorScheme={ESTATUS_COLORS[resultado.estatus] || 'gray'} fontSize="md" px={4} py={2} borderRadius="full">
                    {ESTATUS_LABELS[resultado.estatus] || resultado.estatus}
                  </Badge>
                </HStack>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                  <Box>
                    <Text fontSize="xs" color="gray.500">EXPEDIENTE</Text>
                    <Text fontFamily="mono">{resultado.expediente || 'Sin asignar'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">TIPO DE TRÁMITE</Text>
                    <Text>{resultado.tipoTramite}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">TIPO DE DOCUMENTO</Text>
                    <Text>{resultado.tipoDocumento || '—'}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">SOLICITANTE</Text>
                    <Text>{resultado.solicitante}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">FECHA DE SOLICITUD</Text>
                    <Text>{new Date(resultado.fechaSolicitud).toLocaleDateString('es-MX', { dateStyle: 'long' })}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="xs" color="gray.500">FECHA DE REGISTRO</Text>
                    <Text>{new Date(resultado.fechaRegistro).toLocaleDateString('es-MX', { dateStyle: 'long' })}</Text>
                  </Box>
                </SimpleGrid>

                {resultado.seguimiento && resultado.seguimiento.length > 0 && (
                  <>
                    <Text fontWeight="bold" mt={2}>Seguimiento del Trámite</Text>
                    <Table size="sm" variant="simple">
                      <Thead>
                        <Tr>
                          <Th>Acción</Th>
                          <Th>Estatus</Th>
                          <Th>Fecha</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {resultado.seguimiento.map((s: any, i: number) => (
                          <Tr key={i}>
                            <Td fontSize="xs">{s.accion}</Td>
                            <Td>
                              <Badge colorScheme={ESTATUS_COLORS[s.estatus] || 'gray'} fontSize="xs">
                                {s.estatus || '—'}
                              </Badge>
                            </Td>
                            <Td fontSize="xs">{new Date(s.fecha).toLocaleDateString('es-MX')}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </>
                )}

                {resultado.inspecciones && resultado.inspecciones.length > 0 && (
                  <>
                    <Text fontWeight="bold">Inspecciones</Text>
                    {resultado.inspecciones.map((i: any, idx: number) => (
                      <HStack key={idx} p={2} bg="gray.50" borderRadius="md" wrap="wrap">
                        <Text fontSize="xs">{i.tipo || 'Inspección'}</Text>
                        <Text fontSize="xs" color="gray.500">
                          {new Date(i.fechaProgramada).toLocaleDateString('es-MX')}
                        </Text>
                        <Badge colorScheme={i.resultado === 'APROBADO' ? 'green' : i.resultado === 'REPROBADO' ? 'red' : 'yellow'} fontSize="xs">
                          {i.resultado || 'PENDIENTE'}
                        </Badge>
                      </HStack>
                    ))}
                  </>
                )}
              </VStack>
            )}
          </CardBody>
        </Card>

        {/* SECCIÓN 2: VALIDAR DOCUMENTO */}
        <Card w="full" shadow="md">
          <CardHeader bg="gray.100" borderTopRadius="md">
            <Heading size="sm" color="teal.600">Validar Autenticidad de Documento</Heading>
            <Text fontSize="sm" color="gray.500" mt={1}>
              Escanee el código QR del documento o ingrese el folio de validación
            </Text>
          </CardHeader>
          <CardBody>
            <InputGroup mb={4}>
              <Input
                placeholder="VOPTOT-20260526-A7F92KXZ"
                value={folioValidacion}
                onChange={e => setFolioValidacion(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleValidar()}
                fontFamily="mono"
                textTransform="uppercase"
              />
              <InputRightElement width="5rem" height="full">
                <Button size="sm" colorScheme="teal" onClick={handleValidar} isLoading={loadingVal}>
                  Validar
                </Button>
              </InputRightElement>
            </InputGroup>

            {loadingVal && <Flex justify="center"><Spinner color="teal.500" /></Flex>}

            {errorVal && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                {errorVal}
              </Alert>
            )}

            {validacion && (
              <VStack align="stretch" spacing={3}>
                <Alert
                  status={validacion.valido ? 'success' : validacion.razon === 'VENCIDO' ? 'warning' : 'error'}
                  borderRadius="md"
                >
                  <AlertIcon as={validacion.valido ? CheckCircleIcon : WarningIcon} />
                  <Box ml={2}>
                    <Text fontWeight="bold" fontSize="md">
                      {validacion.valido ? '✔ DOCUMENTO VÁLIDO' :
                        validacion.razon === 'VENCIDO' ? '⚠ VIGENCIA VENCIDA' :
                        validacion.razon === 'CANCELADO' ? '✖ DOCUMENTO CANCELADO' :
                        validacion.razon === 'NO_ENCONTRADO' ? '✖ DOCUMENTO NO ENCONTRADO' :
                        '✖ DOCUMENTO NO VÁLIDO'}
                    </Text>
                    <Text fontSize="sm">{validacion.mensaje}</Text>
                  </Box>
                </Alert>

                {validacion.folio && (
                  <Box bg="white" p={4} borderRadius="md" borderWidth="1px" borderColor="gray.200">
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                      <Box>
                        <Text fontSize="xs" color="gray.500">FOLIO DEL TRÁMITE</Text>
                        <Text fontFamily="mono" fontWeight="bold">{validacion.folio}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">TIPO DE TRÁMITE</Text>
                        <Text fontSize="sm">{validacion.tipoTramite}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">TIPO DE DOCUMENTO</Text>
                        <Text fontSize="sm">{validacion.tipoDocumento || '—'}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">SOLICITANTE</Text>
                        <Text fontSize="sm">{validacion.solicitante}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">ESTATUS DEL TRÁMITE</Text>
                        <Badge colorScheme={ESTATUS_COLORS[validacion.estatus] || 'gray'}>
                          {ESTATUS_LABELS[validacion.estatus] || validacion.estatus}
                        </Badge>
                      </Box>
                      <Box>
                        <Text fontSize="xs" color="gray.500">FECHA DE REGISTRO</Text>
                        <Text fontSize="sm">{new Date(validacion.fechaRegistro).toLocaleDateString('es-MX', { dateStyle: 'long' })}</Text>
                      </Box>
                      {validacion.vigencia && (
                        <>
                          <Box>
                            <Text fontSize="xs" color="gray.500">FECHA DE EMISIÓN</Text>
                            <Text fontSize="sm">{new Date(validacion.vigencia.fechaEmision).toLocaleDateString('es-MX', { dateStyle: 'long' })}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="xs" color="gray.500">VIGENCIA</Text>
                            {validacion.vigencia.fechaVencimiento ? (
                              <HStack spacing={2}>
                                <Text fontSize="sm">{new Date(validacion.vigencia.fechaVencimiento).toLocaleDateString('es-MX', { dateStyle: 'long' })}</Text>
                                <Badge colorScheme={validacion.vigencia.estatusVigencia === 'VIGENTE' ? 'green' : validacion.vigencia.estatusVigencia === 'VENCIDO' ? 'red' : 'gray'}>
                                  {validacion.vigencia.estatusVigencia}
                                </Badge>
                              </HStack>
                            ) : (
                              <Badge colorScheme="blue">Sin vencimiento</Badge>
                            )}
                          </Box>
                        </>
                      )}
                    </SimpleGrid>
                    <Divider mt={3} mb={2} />
                    <Text fontSize="xs" color="gray.400" textAlign="center">
                      Consultado el {validacion.consultadoEn ? new Date(validacion.consultadoEn).toLocaleString('es-MX') : new Date().toLocaleString('es-MX')}
                    </Text>
                  </Box>
                )}
              </VStack>
            )}
          </CardBody>
        </Card>

        <HStack justify="center" spacing={3} flexWrap="wrap">
          <Button size="xs" variant="outline" colorScheme="orange" onClick={() => navigate('/portal/obras/pre-registro')}>
            Hacer Pre-registro
          </Button>
          <Button size="xs" variant="outline" colorScheme="blue" onClick={() => navigate('/portal/obras/tramite-digital')}>
            Trámite Digital
          </Button>
          <Button size="xs" variant="ghost" onClick={() => navigate('/portal/obras')}>
            Portal Principal
          </Button>
        </HStack>
        <Text fontSize="xs" color="gray.400" textAlign="center">
          Dirección de Obras Públicas y Desarrollo Urbano · H. Ayuntamiento de Totolac · Tlaxcala
          <br />
          Para aclaraciones, acuda a las oficinas del Ayuntamiento o llame al teléfono oficial.
        </Text>
      </VStack>
    </Box>
  );
};

export default ConsultaFolioOP;
